import { CurrentSeason, Seasons } from 'common';
import Data from 'data';
import { cubeGetType } from './cube';
import { itemSetId } from './itemReducers';
import { profileFix } from './profileReducers';

export const slotMap = {
  "mainHand": "mainhand",
  "offHand": "offhand",
  "leftFinger": "leftfinger",
  "rightFinger": "rightfinger",
  "bracers": "wrists",
  "special": "follower",
};
export const typeMap = {
  "legs": "pants",
  "ceremonialdagger": "ceremonialknife",
  "flail1H": "flail",
  "mightyweapon1h": "mightyweapon",
  "combatstaff": "daibo",
  "handxbow": "handcrossbow",
  "genericbelt": "belt",
  "belt_barbarian": "mightybelt",
  "orb": "source",
};
export const classMap = {
  "witch-doctor": "witchdoctor",
  "demon-hunter": "demonhunter",
};

const augmentRegex = {
  "caldesanns_vit": /\+(\d+) Vitality/,
  "caldesanns_dex": /\+(\d+) Dexterity/,
  "caldesanns_str": /\+(\d+) Strength/,
  "caldesanns_int": /\+(\d+) Intelligence/,
};

function parseNumber(str) {
  var res = parseFloat(str.replace(/,/g, ""));
  if (isNaN(res)) return 0;
  return res;
}

function matchStat(stat, text, lines, pos) {
  if (stat.base) return null;
  if (stat.caldesanns) return null;
  let result = stat.regex.exec(text) || stat.altregex?.exec(text);
  if (!result) return null;
  if (stat.lines) {
    text = lines.slice(pos, pos + stat.lines).join('\n');
    result = stat.regexFull.exec(text);
  }
  const out = [];
  for (let i = 0; i < stat.args; i++) {
    out.push(parseNumber(result[i + 1]));
  }
  if (stat.args < 0) {
    const passive = Object.entries(Data.allPassives).find(([, [, passive]]) => passive.defaultName === result[1]);
    out.push(passive?.[0] || "blur");
  }
  return out;
}

function parseAttribute(lines, pos, customStat) {
  let text = lines[pos];
  text = text.trim().replace(/\u2013|\u2014/g, "-");
  for (const stat in Data.stats) {
    const values = matchStat(Data.stats[stat], text);
    if (values) return {stat, values};
  }
  if (customStat) {
    const values = matchStat(customStat, text, lines, pos);
    if (values) return {stat: "custom", values, next: pos + (customStat.lines || 1)};
  }
}

function parseItem(data, charClass) {
  if (!data.type) {
    return;
  }

  let result = {
    id: data.id,
    stats: {},
  };

  if (data.transmog) result.transmog = data.transmog.id;
  if (data.dye) result.dye = data.dye.id;

  if (!Data.itemById[data.id]) {
    let typeid = null;
    if (data.type.id) {
      typeid = data.type.id.toLowerCase();
      typeid = (typeMap[typeid] || typeid);
      typeid = typeid.replace(/(generic|_.*)/g, "");
      typeid = (typeMap[typeid] || typeid);
      if (typeid === 'templarspecial') typeid = 'templarrelic';
      if (typeid === 'scoundrelspecial') typeid = 'scoundreltoken';
      if (typeid === 'enchantressspecial') typeid = 'enchantressfocus';
    } else {
      return;
    }
    if (Data.itemTypes[typeid]) {
      result.id = Data.itemTypes[typeid].generic;
    }
  }
  //if (data.attributesRaw.Ancient_Rank && data.attributesRaw.Ancient_Rank.max) {
  //  result.ancient = (data.attributesRaw.Ancient_Rank.max === 2.0 ? "primal" : true);
  //}
  if (data.typeName.indexOf("Primal Ancient") >= 0) {
    result.ancient = "primal";
  } else if (data.typeName.indexOf("Ancient") >= 0) {
    result.ancient = true;
  }

  const item = Data.itemById[result.id];

  let customStat;
  let itemRequired = {};
  if (item) {
    itemRequired = Data.getItemAffixesById(result.id, result.ancient, "only") || {};
    if (itemRequired.custom) {
      customStat = {...itemRequired.custom};
      if (customStat.args == null) {
        customStat.args = 1;
      }
    }
  }

  let adjustDamage = false;
  if (data.attributes && Object.keys(data.attributes).length) {
    for (const type in data.attributes) {
      let pos = 0;
      while (pos < data.attributes[type].length) {
        const res = parseAttribute(data.attributes[type], pos, customStat);
        if (res) {
          result.stats[res.stat] = res.values;
          if (data.attributesHtml?.[type]?.[pos]?.includes("tooltip-icon-enchant")) {
            result.enchant = res.stat;
          }

          pos = (res.next == null ? pos + 1 : res.next);
        } else {
          pos += 1;
        }
      }
    }
  } else if (item.quality === 'ethereal') {
    result = itemSetId(result, item.id, charClass);
    adjustDamage = true;
  }

  result.gems = [];
  if (data.gems) {
    for (const gem of data.gems) {
      if (gem.isGem || gem.isJewel) {
        const info = Data.gemById[gem.item.id];
        if (info && gem.isJewel) info[1] = gem.jewelRank;
        if (info) result.gems.push(info);
      }
    }
    result.stats.sockets = [data.gems.length];
  }

  if (adjustDamage && Data.itemTypes[item.type]?.weapon) {
    let minDamage = data.minDamage || 0;
    let maxDamage = data.maxDamage || 0;
    if (result.stats.damage) {
      minDamage = Math.round(minDamage / (1 + 0.01 * result.stats.damage[0]));
      maxDamage = Math.round(maxDamage / (1 + 0.01 * result.stats.damage[0]));
    }

    let weapon = Data.itemTypes[item.type]?.weapon;
    if (item.weapon) weapon = Object.assign({}, weapon, item.weapon);
    let minBase = weapon.min;
    let maxBase = weapon.max;
    if (result.gems) {
      for (const gem of result.gems) {
        if (gem && gem[1] === "ruby") {
          minBase += Data.gemColors.ruby.weapon.amount[gem[0]];
          maxBase += Data.gemColors.ruby.weapon.amount[gem[0]];
        }
      }
    }

    const damageStat = Object.keys(result.stats).find(stat => Data.stats[stat].damage);
    if (damageStat) {
      let minDamage = data.minDamage || 0;
      let maxDamage = data.maxDamage || 0;
      if (result.stats.damage) {
        minDamage = Math.round(minDamage / (1 + 0.01 * result.stats.damage[0]));
        maxDamage = Math.round(maxDamage / (1 + 0.01 * result.stats.damage[0]));
      }
      result.stats[damageStat][0] = minDamage - minBase;
      result.stats[damageStat][1] = maxDamage - maxBase;
    } else if (result.stats.damage) {
      const multi = ((minDamage / minBase) + (maxDamage / maxBase)) / 2;
      result.stats.damage[0] = Math.round(multi * 100 - 100);
    }
  }

  if (data.armor) {
    result.stats.basearmor = [Math.floor(data.armor)];
  } else if (itemRequired.basearmor) {
    result.stats.basearmor = [itemRequired.basearmor.max];
  }
  if (data.blockChance) {
    const bc = data.blockChance.match(/\+([\d,.]+)% Chance to Block\n([\d,]+)-([\d,]+) Block Amount/);
    if (bc) {
      result.stats.blockamount = [parseNumber(bc[2]), parseNumber(bc[3])];
      result.stats.baseblock = [parseNumber(bc[1])];
      if (result.stats.block) result.stats.baseblock[0] -= result.stats.block[0];
    }
  } else if (itemRequired.blockamount && itemRequired.baseblock) {
    result.stats.blockamount = [itemRequired.blockamount.max, itemRequired.blockamount.max2];
    result.stats.blockamount = [itemRequired.baseblock.max];
  }
  //if (data.attributesRaw.Sockets) {
  //  result.stats.sockets = [data.attributesRaw.Sockets.max];
  //}
  //if (data.attributesRaw.ConsumableAddSockets) {
  //  result.stats.sockets = [data.attributesRaw.ConsumableAddSockets.max];
  //}
  //if (data.attributesRaw.Damage_Percent_Reduction_From_Ranged) {
  //  result.stats.rangedef = [data.attributesRaw.Damage_Percent_Reduction_From_Ranged.max * 100];
  //}
  if (data.augmentation) {
    for (const type in augmentRegex) {
      const m = data.augmentation.match(augmentRegex[type]);
      if (m) result.stats[type] = [parseNumber(m[1])];
    }
  }

  if (item?.quality !== 'ethereal') {
    result.imported = {
      enchant: result.enchant,
      stats: result.stats,
    };
  }
  
  return result;
}

export default function importProfile(data) {
  const charClass = classMap[data.class] || data.class;
  const result = {
    name: data.name,
    class: charClass,
    gender: (data.gender === 0 ? "male" : "female"),
    skills: [...Array(6)],
    passives: [...Array(4)],
    kanai: {},
    paragon: {
      level: 0,
      data: [...Array(4)].map(() => [...Array(4)].map(() => 0)),
    },
    seasonal: (data.seasonal ? CurrentSeason : 0),
    items: {},
    active: {},
    statPriority: [
      {stat: "sockets", options: {}, id: 1},
      {stat: "dps", options: {}, id: 2},
      {stat: "toughness", options: {}, id: 3},
    ],
  };

  for (const slot in data.items) {
    result.items[slotMap[slot] || slot] = parseItem(data.items[slot], charClass);
  }
  if (data.skills && data.skills.active) {
    data.skills.active.forEach(({skill, rune}, index) => {
      const id = skill && Data.skillById[charClass][skill.slug];
      if (id) result.skills[index] = [id, rune?.type || "x"];
    });
  }
  if (data.skills && data.skills.passive) {
    data.skills.passive.forEach(({skill}, index) => {
      const id = skill && Data.skillById[charClass][skill.slug];
      if (id) result.passives[index] = id;
    });
  }
  if (data.paragonLevel) {
    result.paragon.level = data.paragonLevel;
    if (data.paragonLevel >= 800) {
      result.paragon.data[1] = [50, 50, 50, 50];
      result.paragon.data[2] = [50, 50, 50, 50];
      result.paragon.data[3] = [50, 50, 50, 50];
    }
  }
  if (data.legendaryPowers) {
    if (result.seasonal === Seasons.Archives) {
      const types = ["weapon", "armor", "jewelry"];
      let index = 0;
      for (const power of data.legendaryPowers) {
        const type = power && types[index++];
        if (type) result.kanai[type] = power.id;
      }
    } else {
      for (const power of data.legendaryPowers) {
        const type = power && cubeGetType(power.id);
        if (type) {
          if (result.kanai[type]) result.kanai.extra = power.id;
          else result.kanai[type] = power.id;
        }
      }
    }
  }

  const followers = {};
  if (data.followers) {
    for (const [type, follower] of Object.entries(data.followers)) {
      if (!follower.items || !Object.values(follower.items).some(Boolean)) continue;
      if (!Data.followerSkillList[type]) continue;

      const dst = {skills: [...Array(4)], items: {}};
      if (follower.skills) for (const skill of follower.skills) {
        const index = Data.followerSkillList[type].findIndex(id => {
          const info = Data.followerSkills[id];
          return info && info.id === skill.slug;
        });
        if (index >= 0) {
          dst.skills[Math.floor(index / 2)] = Data.followerSkillList[type][index];
        }
      }

      if (follower.items) for (const slot in follower.items) {
        if (follower.items[slot]) {
          const itemData = data.followerItems?.[type]?.[slot];
          if (itemData) {
            dst.items[slotMap[slot] || slot] = parseItem(itemData);
          } else {
            dst.items[slotMap[slot] || slot] = follower.items[slot].id;
          }
        }
      }

      followers[type] = dst;
    }
  }

  return {profile: profileFix(result), followers};
}

export function exportProfile(profile, history, name) {
  profile = {...profile};
  profile.values = history?.values;
  if (name != null) {
    profile.name = name;
  }

  const setCount = {};
  let hasRorg = 0;
  profile.items = {...profile.items};
  for (const slot in profile.items) {
    const data = profile.items[slot];
    if (!data) {
      delete profile.items[slot];
      continue;
    }
    const item = data && Data.itemById[data.id];
    if (item && item.set && Data.itemSets[item.set]) {
      setCount[item.set] = (setCount[item.set] || 0) + 1;
    }
    if (item?.required?.custom?.id === "leg_ringofroyalgrandeur") {
      hasRorg = 1;
    }
  }
  for (const id in setCount) {
    if (setCount[id] + hasRorg >= 6) {
      profile.mainset = id;
    }
  }

  delete profile.id;
  return profile;
}

export function exportAllProfiles(state, historyState, name) {
  state = {...state, name};
  state.profiles = state.profiles.map(profile => exportProfile(profile, historyState[profile.id]));
  const profile = state.profiles[state.activeProfile];
  if (profile) {
    state.class = profile.class;
    state.mainset = profile.mainset;
  }
  return state;
}
